import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Building2, Library, FolderKanban, Users, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/lib/organization-context';
import { SignedImage } from '@/components/ui/signed-image';

interface TeamMemberWelcomeProps {
  onComplete: () => void;
  onBack?: () => void;
}

interface OrgStats {
  libraryCount: number;
  projectCount: number;
  memberCount: number;
}

export function TeamMemberWelcome({ onComplete }: Omit<TeamMemberWelcomeProps, 'onBack'>) {
  const { organization } = useOrganization();
  const [stats, setStats] = useState<OrgStats>({ libraryCount: 0, projectCount: 0, memberCount: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (organization?.id) {
      fetchOrgStats();
    }
  }, [organization?.id]);

  const fetchOrgStats = async () => {
    if (!organization?.id) return;

    try {
      // Fetch library counts
      const [qaRes, caseRes, sectionRes, pricingRes, projectRes, memberRes] = await Promise.all([
        supabase.from('content_library').select('*', { count: 'exact', head: true }).eq('organization_id', organization.id),
        supabase.from('case_studies').select('*', { count: 'exact', head: true }).eq('organization_id', organization.id),
        supabase.from('company_sections').select('*', { count: 'exact', head: true }).eq('organization_id', organization.id),
        supabase.from('pricing_templates').select('*', { count: 'exact', head: true }).eq('organization_id', organization.id),
        supabase.from('projects').select('*', { count: 'exact', head: true }).eq('organization_id', organization.id),
        supabase.from('organization_members').select('*', { count: 'exact', head: true }).eq('organization_id', organization.id).not('joined_at', 'is', null),
      ]);

      const libraryCount = (qaRes.count || 0) + (caseRes.count || 0) + (sectionRes.count || 0) + (pricingRes.count || 0);
      
      setStats({
        libraryCount,
        projectCount: projectRes.count || 0,
        memberCount: memberRes.count || 0,
      });
    } catch (error) {
      console.error('Error fetching org stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { icon: Library, label: 'Library Items', value: stats.libraryCount, color: 'text-primary' },
    { icon: FolderKanban, label: 'Active Proposals', value: stats.projectCount, color: 'text-[hsl(var(--accent-orange))]' },
    { icon: Users, label: 'Team Members', value: stats.memberCount, color: 'text-emerald-500' },
  ];

  return (
    <>
      {/* Header */}
      <div className="text-center mb-8">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.15, type: 'spring', stiffness: 200 }}
          className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/10 mb-6"
        >
          {organization?.logo_url ? (
            <SignedImage 
              bucket="organization-assets"
              pathOrUrl={organization.logo_url}
              alt={organization.name} 
              className="w-12 h-12 object-contain"
              fallback={<Building2 className="w-10 h-10 text-primary" />}
            />
          ) : (
            <Building2 className="w-10 h-10 text-primary" />
          )}
        </motion.div>
        
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="font-editorial text-3xl font-semibold text-foreground mb-3 tracking-tight"
        >
          Welcome to{' '}
          <span className="text-shimmer">{organization?.name || 'the team'}</span>!
        </motion.h1>
        
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="text-muted-foreground/80"
        >
          Your workspace is already set up and ready to go. Here's what your team has built:
        </motion.p>
      </div>

      {/* Stats Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="grid grid-cols-3 gap-4 mb-8"
      >
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.35 + index * 0.1 }}
            className="bg-muted/30 border border-border/50 rounded-xl p-5 text-center backdrop-blur-sm"
          >
            <stat.icon className={`w-6 h-6 mx-auto mb-3 ${stat.color}`} />
            <div className="text-2xl font-bold text-foreground">
              {loading ? '—' : stat.value}
            </div>
            <div className="text-xs text-muted-foreground mt-1">{stat.label}</div>
          </motion.div>
        ))}
      </motion.div>

      {/* What you can do */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-gradient-to-br from-[hsl(var(--accent-orange))]/5 to-primary/5 rounded-xl p-6 mb-8 border border-[hsl(var(--accent-orange))]/10"
      >
        <div className="flex items-start gap-4">
          <div className="p-2.5 rounded-xl bg-[hsl(var(--accent-orange))]/10">
            <Sparkles className="w-5 h-5 text-[hsl(var(--accent-orange))]" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground mb-1">You're all set!</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              You can start contributing to projects, access the shared library, and collaborate with your team right away.
            </p>
          </div>
        </div>
      </motion.div>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <Button 
          onClick={onComplete}
          className="w-full h-12 text-base font-medium bg-[hsl(var(--accent-orange))] hover:bg-[hsl(var(--accent-orange-hover))] text-white shadow-lg shadow-[hsl(var(--accent-orange))]/25 hover:shadow-xl hover:shadow-[hsl(var(--accent-orange))]/30 transition-all"
          size="lg"
        >
          Explore the Dashboard
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </motion.div>
    </>
  );
}
